// frontend/src/pages/WorkOptimizationTimingPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Space, Tag, Typography, message } from 'antd';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Operation, TimingSession, OperationCategory,
  getSession, getOperations, addEvent, completeSession,
} from '../api/workOptimization';

const { Title, Text } = Typography;

const CATEGORY_COLORS: Record<string, string> = {
  productive: '#52c41a', walking: '#fa8c16',
  waiting: '#fadb14', preparation: '#1677ff', loss: '#ff4d4f',
};

const CATEGORY_LABELS: Record<string, string> = {
  productive: 'Полезная работа', walking: 'Ходьба/поиск',
  waiting: 'Ожидание', preparation: 'Подготовка', loss: 'Потеря',
};

const fmtTimer = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const WorkOptimizationTimingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<TimingSession | null>(null);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [currentOp, setCurrentOp] = useState<{ name: string; category: OperationCategory } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [newOpModal, setNewOpModal] = useState(false);
  const [newOpName, setNewOpName] = useState('');
  const [newOpCategory, setNewOpCategory] = useState<OperationCategory>('productive');
  const [completing, setCompleting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!id) return;
    getSession(Number(id)).then(s => {
      setSession(s);
      getOperations(s.work_type.id).then(setOperations);
      // restore elapsed
      const start = new Date(s.started_at).getTime();
      setElapsed(Math.floor((Date.now() - start) / 1000));
    });
  }, [id]);

  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const tapOperation = async (op: Operation) => {
    if (!session) return;
    try {
      await addEvent(session.id, {
        operation_id: op.id,
        category: op.category,
        timestamp: new Date().toISOString(),
      });
      setCurrentOp({ name: op.name, category: op.category });
    } catch { message.error('Ошибка записи операции'); }
  };

  const tapCustom = async () => {
    if (!session || !newOpName.trim()) return;
    try {
      await addEvent(session.id, {
        custom_name: newOpName.trim(),
        category: newOpCategory,
        timestamp: new Date().toISOString(),
      });
      setCurrentOp({ name: newOpName.trim(), category: newOpCategory });
      setNewOpModal(false);
      setNewOpName('');
    } catch { message.error('Ошибка записи операции'); }
  };

  const handleComplete = async () => {
    if (!session) return;
    setCompleting(true);
    try {
      await completeSession(session.id);
      navigate(`/work-optimization/session/${session.id}/analysis`);
    } catch {
      message.error('Ошибка завершения сессии');
      setCompleting(false);
    }
  };

  if (!session) return null;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '0 8px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{session.work_type.name}</Title>
        <Text style={{ fontSize: 32, fontWeight: 'bold', fontFamily: 'monospace' }}>
          {fmtTimer(elapsed)}
        </Text>
      </div>

      {/* Current operation */}
      <div style={{
        background: currentOp ? CATEGORY_COLORS[currentOp.category] + '22' : '#f5f5f5',
        border: `2px solid ${currentOp ? CATEGORY_COLORS[currentOp.category] : '#d9d9d9'}`,
        borderRadius: 8, padding: '8px 12px', marginBottom: 16, textAlign: 'center',
      }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Текущая операция:</Text>
        <div style={{ fontWeight: 'bold', fontSize: 16 }}>
          {currentOp?.name || '—'}
        </div>
        {currentOp && (
          <Tag color={CATEGORY_COLORS[currentOp.category]} style={{ marginTop: 4 }}>
            {CATEGORY_LABELS[currentOp.category]}
          </Tag>
        )}
      </div>

      {/* Operation buttons */}
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {operations.map(op => (
          <Button
            key={op.id}
            block
            size="large"
            style={{
              height: 56,
              borderLeft: `6px solid ${CATEGORY_COLORS[op.category]}`,
              textAlign: 'left',
              fontWeight: currentOp?.name === op.name ? 'bold' : 'normal',
              background: currentOp?.name === op.name ? CATEGORY_COLORS[op.category] + '22' : undefined,
            }}
            onClick={() => tapOperation(op)}
          >
            {op.name}
          </Button>
        ))}

        <Button
          block size="large" icon={<PlusOutlined />}
          style={{ height: 56, borderStyle: 'dashed' }}
          onClick={() => setNewOpModal(true)}
        >
          Новая операция...
        </Button>
      </Space>

      {/* Complete button */}
      <Button
        type="primary" danger block size="large"
        icon={<CheckOutlined />}
        style={{ marginTop: 24, height: 56 }}
        loading={completing}
        onClick={handleComplete}
      >
        Завершить сессию
      </Button>

      {/* New operation modal */}
      <Modal
        title="Добавить операцию"
        open={newOpModal}
        onOk={tapCustom}
        onCancel={() => setNewOpModal(false)}
        okText="Добавить"
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Input
            placeholder="Название операции"
            value={newOpName}
            onChange={e => setNewOpName(e.target.value)}
            onPressEnter={tapCustom}
            autoFocus
          />
          <div>
            <Text type="secondary">Категория:</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {(Object.entries(CATEGORY_LABELS) as [OperationCategory, string][]).map(([cat, label]) => (
                <Tag
                  key={cat}
                  color={newOpCategory === cat ? CATEGORY_COLORS[cat] : undefined}
                  style={{ cursor: 'pointer', fontSize: 14, padding: '4px 12px' }}
                  onClick={() => setNewOpCategory(cat)}
                >
                  {label}
                </Tag>
              ))}
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default WorkOptimizationTimingPage;
